import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { formatPlate } from "../../utility/funcs";
import {
  AppProvider,
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  InlineStack,
  Select,
  Spinner,
  Text,
  TextField,
} from "@shopify/polaris";
import esTranslations from "@shopify/polaris/locales/es.json";
import { CameraIcon, ImageIcon, ClipboardIcon } from "@shopify/polaris-icons";
import {
  makeCaptureClient,
  compressImage,
  PickPhoto,
  Thumb,
} from "../Services/captureKit";

// Public page for a trasladista (/gastos/:token): photograph each ticket paid
// with their own card, confirm the total, and see exactly what to type on the
// merchant's invoicing portal (GoMove's fiscal data + our expenses email). The
// CFDI goes straight to that mailbox; a ticket is reimbursed once its CFDI
// arrives and matches. Food is not ticketed — it's paid as a fixed allowance.

const call = makeCaptureClient("traslado_capture");

const KIND_OPTIONS = [
  { label: "Gasolina", value: "gasolina" },
  { label: "Caseta", value: "peaje" },
  { label: "Regreso (autobús, avión, taxi)", value: "regreso" },
  { label: "Herramienta / refacción", value: "herramienta" },
  { label: "Otro", value: "otro" },
];
const KIND_LABEL = Object.fromEntries(KIND_OPTIONS.map((k) => [k.value, k.label]));

const money = (n) =>
  n == null || n === ""
    ? "—"
    : `$${Number(n).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function CopyRow({ label, value }) {
  const [copied, setCopied] = useState(false);
  return (
    <InlineStack align="space-between" blockAlign="center" wrap={false} gap="200">
      <BlockStack gap="0">
        <Text tone="subdued" variant="bodySm">
          {label}
        </Text>
        <Text fontWeight="semibold">{value}</Text>
      </BlockStack>
      <Button
        size="slim"
        icon={ClipboardIcon}
        accessibilityLabel={`Copiar ${label}`}
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          } catch {
            /* clipboard blocked */
          }
        }}
      >
        {copied ? "¡Copiado!" : null}
      </Button>
    </InlineStack>
  );
}

function TicketCard({ ticket, original, editable, api, onChange, onDelete, onDeleteOriginal }) {
  const [total, setTotal] = useState(ticket.total != null ? String(ticket.total) : "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const locked = ticket.status !== "pending_cfdi" || !editable;

  const save = async (patch) => {
    setSaving(true);
    setError(null);
    try {
      const res = await api({ action: "update", id: ticket.id, ...patch });
      onChange(res.ticket);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const statusBadge = {
    pending_cfdi: <Badge tone="attention">Falta factura</Badge>,
    cfdi_matched: <Badge tone="info">Factura recibida</Badge>,
    needs_review: <Badge tone="info">Factura recibida · en revisión</Badge>,
    approved: <Badge tone="success">Aprobado</Badge>,
    rejected: <Badge tone="critical">Rechazado</Badge>,
    expired: <Badge tone="critical">Vencido · sin reembolso</Badge>,
  }[ticket.status];
  // Same rule as expire_traslado_tickets: the end of the ticket's month.
  const deadline = (() => {
    const d = ticket.ticket_date ? new Date(`${ticket.ticket_date}T12:00:00`) : new Date();
    return new Date(d.getFullYear(), d.getMonth() + 1, 0).toLocaleDateString("es-MX", {
      day: "numeric",
      month: "long",
    });
  })();

  return (
    <div className="fc-evidence">
      <BlockStack gap="200">
        <InlineStack gap="300" blockAlign="start" wrap={false}>
          <Thumb photo={ticket} onDelete={locked ? null : onDelete} />
          <BlockStack gap="100">
            <InlineStack gap="200" blockAlign="center">
              <Text fontWeight="semibold">{ticket.merchant_name || KIND_LABEL[ticket.kind]}</Text>
              {statusBadge}
            </InlineStack>
            <Text tone="subdued" variant="bodySm">
              {[ticket.ticket_date, ticket.liters ? `${ticket.liters} L` : null, ticket.merchant_rfc]
                .filter(Boolean)
                .join(" · ") || "Sin datos leídos"}
            </Text>
            {ticket.status === "pending_cfdi" && ticket.invoice_mode === "portal" && (
              <Text tone="success" variant="bodySm">
                GoMove pedirá esta factura en línea. No necesitas hacer nada.
              </Text>
            )}
            {ticket.status === "pending_cfdi" && ticket.invoice_mode !== "portal" && (
              <Text tone="critical" variant="bodySm" fontWeight="semibold">
                Este comercio no factura en línea: pide la factura en caja ahora (antes del {deadline}).
              </Text>
            )}
            {ticket.status === "approved" && ticket.cfdi_pending && (
              <Text tone="subdued" variant="bodySm">
                Aprobado; la factura sigue pendiente.
              </Text>
            )}
            {ticket.status === "approved" && ticket.deductible === false && (
              <Text tone="subdued" variant="bodySm">
                Aprobado sin factura.
              </Text>
            )}
            {ticket.status === "rejected" && ticket.review_reason && (
              <Text tone="critical" variant="bodySm">
                {ticket.review_reason}
              </Text>
            )}
            {ticket.ai?.note && ticket.status === "pending_cfdi" && (
              <Text tone="caution" variant="bodySm">
                {ticket.ai.note}
              </Text>
            )}
          </BlockStack>
        </InlineStack>

        <InlineStack gap="200" wrap={false}>
          <div style={{ flex: 1 }}>
            <Select
              label="Tipo"
              options={KIND_OPTIONS}
              value={ticket.kind}
              disabled={locked || saving}
              onChange={(v) => save({ kind: v })}
            />
          </div>
          <div style={{ flex: 1 }}>
            <TextField
              label="Total pagado"
              type="number"
              inputMode="decimal"
              prefix="$"
              autoComplete="off"
              value={total}
              disabled={locked}
              onChange={setTotal}
              onBlur={() => {
                if (total !== "" && Number(total) !== Number(ticket.total)) save({ total: Number(total) });
              }}
              error={!total ? "Escribe el total" : undefined}
            />
          </div>
        </InlineStack>

        {ticket.invoice_mode !== "portal" && ticket.invoice_ref && (
          <CopyRow label="Dato para facturar (del ticket)" value={ticket.invoice_ref} />
        )}
        {ticket.invoice_mode !== "portal" && ticket.ai?.invoice_url && (
          <Button url={/^https?:/i.test(ticket.ai.invoice_url) ? ticket.ai.invoice_url : `https://${ticket.ai.invoice_url}`} external variant="plain">
            Abrir página para facturar
          </Button>
        )}
        {original && !locked && (
          <Banner tone="warning" title="Parece repetido">
            <BlockStack gap="200">
              <Text>
                Ya subiste un ticket de {money(original.total)}
                {original.merchant_name ? ` en ${original.merchant_name}` : ""} con el mismo folio.
              </Text>
              <InlineStack gap="200">
                {original.status === "pending_cfdi" && (
                  <Button size="slim" onClick={onDeleteOriginal}>
                    Conservar este y borrar el otro
                  </Button>
                )}
                <Button size="slim" tone="critical" onClick={onDelete}>
                  Borrar este
                </Button>
                <Button size="slim" variant="plain" loading={saving} onClick={() => save({ action: "not_duplicate" })}>
                  Son distintos
                </Button>
              </InlineStack>
            </BlockStack>
          </Banner>
        )}
        {error && <Banner tone="critical">{error}</Banner>}
      </BlockStack>
    </div>
  );
}

function TrasladoGastos() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });
  const [tickets, setTickets] = useState([]);
  const [uploading, setUploading] = useState(0);
  const [error, setError] = useState(null);

  const tokenCall = useCallback((body) => call({ ...body, token }), [token]);

  useEffect(() => {
    tokenCall({ action: "load" })
      .then((data) => {
        setState({ loading: false, data });
        setTickets(data.tickets || []);
      })
      .catch((err) => setState({ loading: false, error: err.message }));
  }, [tokenCall]);

  const upload = async (file) => {
    setUploading((n) => n + 1);
    setError(null);
    try {
      const blob = await compressImage(file);
      const form = new FormData();
      form.append("action", "upload");
      form.append("token", token);
      form.append("file", blob, "ticket.jpg");
      const res = await call(form);
      setTickets((prev) => [...prev, res.ticket]);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading((n) => n - 1);
    }
  };

  const remove = async (ticket) => {
    try {
      await tokenCall({ action: "delete", id: ticket.id });
      setTickets((prev) =>
        prev
          .filter((t) => t.id !== ticket.id)
          .map((t) => (t.duplicate_of === ticket.id ? { ...t, duplicate_of: null } : t)),
      );
    } catch (err) {
      setError(err.message);
    }
  };

  const shell = (content) => (
    <AppProvider i18n={esTranslations}>
      <div className="fc-page">
        <div className="fc-header">
          <img src="/images/logo_white_256.webp" alt="GoMove" />
          <span className="fc-header-title">Gastos del traslado</span>
        </div>
        <div className="fc-body">{content}</div>
      </div>
    </AppProvider>
  );

  if (state.loading)
    return shell(
      <div className="fc-center">
        <Spinner />
      </div>,
    );
  if (state.error)
    return shell(
      <Banner tone="critical" title="No se pudo abrir la liga">
        <p>{state.error}</p>
      </Banner>,
    );

  const { order, fiscal, editable } = state.data;
  const car = order.car || {};
  const byKind = tickets.reduce((acc, t) => {
    acc[t.kind] = (acc[t.kind] || 0) + Number(t.total || 0);
    return acc;
  }, {});
  const total = Object.values(byKind).reduce((a, b) => a + b, 0);
  const pendingCfdi = tickets.filter((t) => t.status === "pending_cfdi").length;

  return shell(
    <BlockStack gap="400">
      <Card>
        <InlineStack gap="300" blockAlign="center" wrap={false}>
          {car.model?.image_url && <img className="fc-car-img" src={car.model.image_url} alt="" />}
          <BlockStack gap="100">
            <Text variant="headingMd" as="h2">
              {[car.model?.brand?.name, car.model?.name, car.year].filter(Boolean).join(" ")}
            </Text>
            <InlineStack gap="200" blockAlign="center">
              <span className="fc-plate">{formatPlate(car.plate)}</span>
            </InlineStack>
            <Text tone="subdued" variant="bodySm">
              {order.route.filter(Boolean).join(" → ")} · Traslado #{order.id}
            </Text>
          </BlockStack>
        </InlineStack>
      </Card>

      {!editable && (
        <Banner tone="info">Este traslado ya está cerrado; ya no se pueden agregar tickets.</Banner>
      )}

      <Card>
        <BlockStack gap="300">
          <Text variant="headingMd" as="h3">
            Cómo pedir la factura
          </Text>
          <Text tone="subdued">
            Paga con tu tarjeta y toma foto del ticket aquí. Si el ticket trae
            página para facturar, <b>GoMove la pide</b>. Si no, <b>pídela en caja
            en ese momento</b> con estos datos y el correo de GoMove: la factura nos
            llega directo. Sin factura no hay reembolso.
          </Text>
          <CopyRow label="RFC" value={fiscal.rfc} />
          <CopyRow label="Razón social" value={fiscal.razon_social} />
          <CopyRow label="Código postal" value={fiscal.cp} />
          <CopyRow label="Régimen fiscal" value={`${fiscal.regimen} – ${fiscal.regimen_label}`} />
          <CopyRow label="Uso de CFDI" value={`${fiscal.uso_cfdi} – ${fiscal.uso_cfdi_label}`} />
          <CopyRow label="Correo para la factura" value={fiscal.email} />
          <Text tone="subdued" variant="bodySm">
            La comida no lleva ticket: se paga fija junto con tu pago.
          </Text>
        </BlockStack>
      </Card>

      {editable && (
        <Card>
          <BlockStack gap="200">
            <PickPhoto
              icon={CameraIcon}
              variant="primary"
              size="large"
              fullWidth
              onFiles={([f]) => upload(f)}
            >
              Tomar foto de un ticket
            </PickPhoto>
            <PickPhoto
              icon={ImageIcon}
              camera={false}
              multiple
              fullWidth
              onFiles={(files) => files.forEach(upload)}
            >
              Subir desde galería
            </PickPhoto>
            {uploading > 0 && (
              <InlineStack gap="200" blockAlign="center">
                <Spinner size="small" />
                <Text tone="subdued">Leyendo ticket{uploading > 1 ? `s (${uploading})` : ""}…</Text>
              </InlineStack>
            )}
            {error && <Banner tone="critical">{error}</Banner>}
          </BlockStack>
        </Card>
      )}

      {tickets.length > 0 && (
        <Card>
          <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="center">
              <Text variant="headingMd" as="h3">
                Tus tickets ({tickets.length})
              </Text>
              {pendingCfdi > 0 && <Badge tone="attention">{`${pendingCfdi} sin factura`}</Badge>}
            </InlineStack>
            {tickets.map((t) => (
              <TicketCard
                key={t.id}
                ticket={t}
                original={t.duplicate_of ? tickets.find((x) => x.id === t.duplicate_of) : null}
                editable={editable}
                api={tokenCall}
                onChange={(updated) =>
                  setTickets((prev) =>
                    prev.map((x) => (x.id === updated.id ? { ...updated, url: x.url } : x)),
                  )
                }
                onDelete={() => remove(t)}
                onDeleteOriginal={() => remove(tickets.find((x) => x.id === t.duplicate_of))}
              />
            ))}
            <BlockStack gap="100">
              {Object.entries(byKind).map(([k, v]) => (
                <InlineStack key={k} align="space-between">
                  <Text>{KIND_LABEL[k]}</Text>
                  <Text>{money(v)}</Text>
                </InlineStack>
              ))}
              <InlineStack align="space-between">
                <Text fontWeight="bold">Total</Text>
                <Text fontWeight="bold">{money(total)}</Text>
              </InlineStack>
              {order.expected?.fuel_cost != null && (
                <Text tone="subdued" variant="bodySm">
                  Gasolina estimada para esta ruta: {money(order.expected.fuel_cost)}
                  {order.expected.fuel_liters ? ` (${order.expected.fuel_liters} L)` : ""}
                </Text>
              )}
            </BlockStack>
          </BlockStack>
        </Card>
      )}
    </BlockStack>,
  );
}

export default TrasladoGastos;
